import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  EditContactStyled,
  SwipeableDrawerStyled,
} from "./edit-contacts.styles";
import TextInputFormField from "../../components/form-fields/text-input-field/text-input-form-field";
import SelectFormField from "../../components/form-fields/select-field/select-form-field";
import { useForm } from "react-hook-form";
import { Close } from "@mui/icons-material";
import ModalForm from "../../components/modal-form/modal-form";
import { CONTACT_TYPES } from "../../constants";
import { ModalFormButtonsBox } from "../../components/modal-form/modal-form.styles";
import { Button } from "../../components/button/button";
import { useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { EditContactProps } from "./types";
import {
  useDoctorsQuery,
  useOfficesQuery,
  useUpdateContactMutation,
} from "../../generated/graphql";

// Define the form data interface
interface FormData {
  officeId: string;
  doctorId: string;
  type: string;
  name: string;
}

const EditContact: React.FC<EditContactProps> = ({
  id,
  name,
  doctorId,
  officeId,
  isVisible,
  callerPhoneNumber,
  onClose,
  refetch,
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const openNotifications = () => {};

  const DrawerPaperProps: any = {
    style: { position: "absolute" },
  };

  const DrawerModalProps: any = {
    container: document.querySelector(".app"),
    style: { position: "absolute" },
  };

  // Initialize form with empty default values
  const { control, handleSubmit, formState, reset, setValue, watch } =
    useForm<FormData>({
      mode: "all",
      defaultValues: {
        name: "",
        officeId: "",
        doctorId: "",
        type: "",
      },
    });
  const formValues = watch();
  const { data } = useDoctorsQuery({
    variables: { first: 1000, page: 1 },
  });

  const { data: officesData } = useOfficesQuery({
    variables: { first: 1000, page: 1 },
  });

  const doctorOptions = data?.doctors.data.map((doctor) => {
    return { label: doctor.name, value: doctor.id.toString() };
  });

  const officesOptions = officesData?.offices.data.map((office) => {
    return { label: office.name, value: office.id.toString() };
  });

  // Effect to set form values when component opens or props change
  useEffect(() => {
    if (isVisible) {
      // Reset form and set values when drawer opens
      reset({
        name: name ?? "",
        officeId: officeId?.toString() ?? "",
        doctorId: doctorId?.toString() ?? "",
        type: "", // You might want to set a default type here if available
      });
    }
  }, [isVisible, name, doctorId, officeId, reset]);

  // Alternative approach: Set individual values when they become available
  useEffect(() => {
    if (name !== undefined) {
      setValue("name", name || "");
    }
  }, [name, setValue]);

  useEffect(() => {
    if (doctorId !== undefined) {
      setValue("doctorId", doctorId?.toString() || "");
    }
  }, [doctorId, setValue]);

  useEffect(() => {
    if (officeId !== undefined) {
      setValue("officeId", officeId?.toString() || "");
    }
  }, [officeId, setValue]);

  // Set up the mutation properly
  const [updateContact] = useUpdateContactMutation();

  // Use proper form submission with react-hook-form
  const onSubmit = async (formData: FormData) => {
    try {
      setIsLoading(true);

      // Properly structure the mutation variables
      await updateContact({
        variables: {
          id: id?.toString(),
          name: formData.name,
          type: formData.type,
          officeId: Number(formData.officeId),
          doctorId: Number(formData.doctorId),
          phoneNumber: callerPhoneNumber,
        },
      });

      refetch && refetch();
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: "Contact updated successfully.",
        })
      );

      onClose && onClose();
    } catch (err: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: err?.message || "Failed to update contact",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isValid =
    formValues?.name &&
    formValues?.type &&
    (formValues?.officeId || formValues?.doctorId);

  return (
    <>
      <SwipeableDrawerStyled
        anchor="right"
        open={isVisible}
        onClose={onClose}
        onOpen={openNotifications}
        PaperProps={DrawerPaperProps}
        ModalProps={DrawerModalProps}
        transitionDuration={300}
      >
        <EditContactStyled>
          <Box className="d-flex align-items-center justify-content-between header">
            {id ? (
              <Typography className="title">Edit contact</Typography>
            ) : (
              <Typography className="title">Add to contacts</Typography>
            )}
            <span className="close-icon d-inline-flex" onClick={onClose}>
              <Close />
            </span>
          </Box>
          <Box className="fields-wrapper">
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalForm>
                <TextInputFormField
                  control={control}
                  label="Name"
                  placeholder="Enter here"
                  name="name"
                />
                <SelectFormField
                  name="type"
                  control={control}
                  placeholder="Select here"
                  label="Type"
                  options={CONTACT_TYPES.map((x) => {
                    return { label: x, value: x };
                  })}
                />
                <SelectFormField
                  name="officeId"
                  control={control}
                  placeholder="Select here"
                  label="Office"
                  options={officesOptions || []}
                />
                <SelectFormField
                  name="doctorId"
                  control={control}
                  placeholder="Select here"
                  label="Doctor"
                  options={doctorOptions || []}
                />
                <ModalFormButtonsBox>
                  <Button
                    type="submit"
                    disabled={!isValid}
                    isLoading={isLoading}
                  >
                    Save
                  </Button>
                </ModalFormButtonsBox>
              </ModalForm>
            </form>
          </Box>
        </EditContactStyled>
      </SwipeableDrawerStyled>
    </>
  );
};

export default EditContact;
